import React from 'react';
import { useController, useWatch, useForm } from 'react-hook-form';
import { dinero, toDecimal } from 'dinero.js';

import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import {
  Button,
  IconButton,
  Stack,
  Typography,
  Box,
  Divider,
} from '@mui/material';

import formatNumber from 'venuescanner-js/helpers/formatNumber';
import EnquiryInfoAddMessage from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryInfoAddMessage';
import EnquiryCtas from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryCtas/index.js';
import CtaMarkAsBooked from 'venuescanner-js/react/components/Inbox/components/Objects/Ctas/CtaMarkAsBooked';
import Tip from 'venuescanner-js/react/components/Inbox/components/Objects/Tip';
import EnquiryTitle from 'venuescanner-js/react/components/Inbox/components/Enquiry/EnquiryTitle';
import './quote-form.scss';

export function QuoteSummary({
  onSubmit,
  onGoBack,
  submitText,
  vat,
  totalPaidAmount,
  hostAmount,
  extraCharges,
  onMarkAsBooked,
  onCloseEnquiry,
}) {
  const { handleSubmit, register, control } = useForm({
    defaultValues: {
      attachments: [],
      message: '',
    },
  });

  return (
    <>
      <form
        id='quoteForm'
        className='enquiry quote-form'
        onSubmit={handleSubmit(onSubmit)}>
        <EnquiryTitle title='Quote summary' onCancel={onGoBack} />
        <Stack className='enquiry-info' spacing={1} direction='column'>
          <SummaryRow
            name='organiser-will-pay'
            label='Organiser will pay'
            value={toDecimal(dinero(totalPaidAmount))}
          />
          <Divider sx={{ backgroundColor: '#7e8cab' }} />
          {extraCharges}
          <SummaryRow
            bold
            name='you-will-receive'
            label='You will receive'
            value={toDecimal(dinero(hostAmount))}
          />
          {vat && (
            <SummaryRow name='vat' label='VAT' value={toDecimal(dinero(vat))} />
          )}
        </Stack>
        <AddMessageAndAttachments
          sx={{ mt: 1.25 }}
          register={register}
          control={control}
        />
      </form>
      <EnquiryCtas sx={{ pl: 1.5, pr: 1.5 }}>
        <Button
          fullWidth
          form='quoteForm'
          color='primary'
          variant='contained'
          size='medium-large'
          type='submit'>
          {submitText}
        </Button>
        <CtaMarkAsBooked type='button' onClick={onMarkAsBooked} />
        <Button
          size='medium-large'
          fullWidth
          variant='text'
          color='primary'
          onClick={onCloseEnquiry}>
          Close enquiry
        </Button>
        <Tip
          title='Tip'
          text='When you mark an enquiry as booked your venue gets a higher search ranking'
        />
      </EnquiryCtas>
    </>
  );
}

export function SummaryRow({ value, label, name, bold }) {
  return (
    <Stack direction='row' justifyContent='space-between' sx={{ fontSize: 13 }}>
      <Typography sx={{ fontWeight: bold ? 700 : 400 }}>{label}</Typography>
      <Typography id={name} sx={{ fontWeight: bold ? 700 : 400 }}>
        £{formatNumber(value)}
      </Typography>
    </Stack>
  );
}

export function Attachments({ control }) {
  const {
    field: { onChange },
  } = useController({
    name: 'attachments',
    control,
  });

  const attachments = useWatch({ name: 'attachments', control });

  const handleChange = event => {
    const { files } = event.target;

    if (!files.length || !files[0].name) {
      return;
    }

    const updatedFiles = [...attachments, files[0]];

    onChange(updatedFiles);
  };

  const handleDelete = fileName => {
    const itemToDeleteIndex = attachments.findIndex(
      ({ name }) => name === fileName,
    );

    const updatedFiles = [
      ...attachments.slice(0, itemToDeleteIndex),
      ...attachments.slice(itemToDeleteIndex + 1),
    ];

    onChange(updatedFiles);
  };

  return (
    <div className='attachments'>
      {attachments.length > 0 &&
        [...attachments].map((file, index) => (
          <React.Fragment key={`${index}${file.name}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
              <AttachmentItem>
                {file.name}
                <IconButton
                  sx={{ ml: 'auto', p: 0 }}
                  onClick={() => handleDelete(file.name)}
                  aria-labelledby='remove-attachment-label'>
                  <span id='remove-attachment-label' hidden>
                    Delete attachment {file.name}
                  </span>
                  <RemoveCircleOutlineOutlinedIcon
                    sx={{ width: 22, height: 22, color: '#424242' }}
                  />
                </IconButton>
              </AttachmentItem>
            </Box>
            <Divider sx={{ width: '100%', my: 1 }} />
          </React.Fragment>
        ))}
      <input
        type='file'
        className='visually-hidden'
        id='quoteAttachments'
        onChange={handleChange}
      />
      <Box
        component='label'
        sx={{ display: 'flex', valignItems: 'center', fontSize: 16 }}
        htmlFor='quoteAttachments'>
        <AttachmentItem isDefault>
          {attachments.length ? 'Add more' : 'Add attachments'}
        </AttachmentItem>
      </Box>
    </div>
  );
}

function AttachmentItem({ children, isDefault }) {
  return (
    <>
      {isDefault ? (
        <AddCircleOutlineOutlinedIcon
          htmlColor='#424242'
          sx={{ width: 22, height: 22, mr: 0.5 }}
        />
      ) : (
        <FilePresentOutlinedIcon
          sx={{ width: 22, height: 22, mr: 0.5 }}
          htmlColor='#424242'
        />
      )}
      {children}
    </>
  );
}

function AddMessageAndAttachments({ register, control, sx = {} }) {
  return (
    <Stack direction='column' spacing={1.5} sx={sx}>
      <div className='enquiry-info'>
        <EnquiryInfoAddMessage
          label='Message'
          register={register}
          placeholder='Please provide details about what is included in your quote, inclduing relevant package or menu information'
        />
      </div>
      <Box className='enquiry-info' sx={{ py: 1 }}>
        <Attachments control={control} />
      </Box>
    </Stack>
  );
}
